import api, { type CustomAxiosConfig, setAuthHeader } from '@/services/apiService';
import { assertApiSuccess, ApiError, type ApiResponse } from '@/types/api';
import type { AppAuthResponse, AppLoginRequest, AppMeResponse, UserRole } from '@/types/auth';
import type { CoHostCommunity } from '@/types/communityHost';
import {
  clearSession,
  getAccessToken,
  setAccessTokenCookie,
  setSessionHint,
} from '@/utils/auth';
import { getDeviceType, getOrCreateDeviceId } from '@/utils/device';
import { getNativePlatform } from '@/utils/capacitor';
import { appleRedirectUri } from '@/utils/appleRedirect';
import { clearArenaSlug, setArenaSlug } from '@/utils/arena';

export interface RegisterCustomerPaymentPayload {
  name: string;
  email: string;
  phone?: string;
  password: string;
  confirmPassword: string;
}

interface SocialLoginBody {
  token?: string;
  code?: string;
  role: 'customer';
  for_app: true;
  device_type: ReturnType<typeof getDeviceType>;
  device_id: string;
  redirect_uri?: string;
}

const STORAGE_KEYS = {
  role: 'sporto_role',
  slug: 'sporto_slug',
  arena: 'sporto_arena',
  mitraName: 'sporto_mitra_name',
  clientName: 'clientName',
  clientAvatar: 'clientAvatar',
} as const;

class AuthService {
  async loginWithGoogleWeb(code: string): Promise<AppAuthResponse> {
    return this.postSocial('/auth/google', {
      code,
      role: 'customer',
      for_app: true,
      device_type: getDeviceType(),
      device_id: getOrCreateDeviceId(),
    });
  }

  async loginWithGoogleNative(idToken: string): Promise<AppAuthResponse> {
    return this.postSocial('/auth/google-mobile', {
      token: idToken,
      role: 'customer',
      for_app: true,
      device_type: getDeviceType(),
      device_id: getOrCreateDeviceId(),
    });
  }

  async loginWithApple(idToken: string): Promise<AppAuthResponse> {
    const body: SocialLoginBody = {
      token: idToken,
      role: 'customer',
      for_app: true,
      device_type: getDeviceType(),
      device_id: getOrCreateDeviceId(),
    };
    if (getNativePlatform() === 'web') {
      body.redirect_uri = appleRedirectUri();
    }
    return this.postSocial('/auth/apple', body);
  }

  async login(payload: AppLoginRequest): Promise<AppAuthResponse> {
    const body: AppLoginRequest = {
      ...payload,
      identifier: payload.identifier.trim().toLowerCase(),
      device_type: payload.device_type ?? getDeviceType(),
      device_id: payload.device_id ?? getOrCreateDeviceId(),
    };
    try {
      const res = await api.post<ApiResponse<AppAuthResponse>>('/auth/app-login', body);
      assertApiSuccess(res.data);
      const data = res.data.data;
      this.persistSession(data);
      return data;
    } catch (error) {
      throw this.toAuthError(error);
    }
  }

  async registerCustomerPayment(payload: RegisterCustomerPaymentPayload): Promise<AppAuthResponse> {
    try {
      const res = await api.post<ApiResponse<AppAuthResponse>>('/auth/register-customer-payment', {
        name: payload.name,
        email: payload.email.trim().toLowerCase(),
        phone: payload.phone ?? '',
        password: payload.password,
        confirm_password: payload.confirmPassword,
        device_id: getOrCreateDeviceId(),
      });
      assertApiSuccess(res.data);
      return res.data.data;
    } catch (error) {
      throw this.toAuthError(error);
    }
  }

  async refresh(): Promise<AppAuthResponse> {
    const res = await api.post<ApiResponse<AppAuthResponse>>(
      '/auth/refresh',
      { device_id: getOrCreateDeviceId() },
      { _skipAuthInterceptor: true } as CustomAxiosConfig
    );
    assertApiSuccess(res.data);
    const data = res.data.data;
    this.persistSession(data);
    return data;
  }

  async me(): Promise<AppMeResponse> {
    const res = await api.get<ApiResponse<AppMeResponse>>('/auth/app-me');
    assertApiSuccess(res.data);
    return res.data.data;
  }

  async logout(_role?: string): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      this.clearSession();
    }
  }

  async listCoHostCommunities(): Promise<CoHostCommunity[]> {
    const res = await api.get<ApiResponse<CoHostCommunity[]>>('/auth/co-host-communities');
    assertApiSuccess(res.data);
    return Array.isArray(res.data.data) ? res.data.data : [];
  }

  async enterCommunity(communityId: number): Promise<AppAuthResponse> {
    try {
      const res = await api.post<ApiResponse<AppAuthResponse>>('/auth/enter-community', {
        community_id: communityId,
        device_type: getDeviceType(),
        device_id: getOrCreateDeviceId(),
      });
      assertApiSuccess(res.data);
      const data = res.data.data;
      this.persistSession(data);
      return data;
    } catch (error) {
      throw this.toAuthError(error);
    }
  }

  async exitCommunity(): Promise<AppAuthResponse> {
    try {
      const res = await api.post<ApiResponse<AppAuthResponse>>('/auth/exit-community', undefined, {
        _skipCoHostRevokedHandler: true,
      } as CustomAxiosConfig);
      assertApiSuccess(res.data);
      const data = res.data.data;
      this.persistSession(data);
      return data;
    } catch (error) {
      throw this.toAuthError(error);
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      const res = await api.delete<ApiResponse<null>>('/delete-account');
      assertApiSuccess(res.data);
    } catch (error) {
      const mapped = this.toAuthError(error);
      throw mapped.message ? mapped : new ApiError('Gagal menghapus akun');
    }
  }

  private async postSocial(path: string, body: SocialLoginBody): Promise<AppAuthResponse> {
    try {
      const res = await api.post<ApiResponse<AppAuthResponse>>(path, body);
      assertApiSuccess(res.data);
      const data = res.data.data;
      this.persistSession(data);
      return data;
    } catch (error) {
      throw this.toAuthError(error);
    }
  }

  persistSession(data: AppAuthResponse): void {
    setAccessTokenCookie(data.token, data.exp);
    setSessionHint();
    setAuthHeader(data.token);
    this.storeUserMeta(data);
  }

  clearSession(): void {
    clearSession();
    setAuthHeader(null);
    this.clearUserMeta();
  }

  private storeUserMeta(data: AppAuthResponse): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.role, data.role);
    if (data.slug) {
      localStorage.setItem(STORAGE_KEYS.slug, data.slug);
      setArenaSlug(data.slug);
    }
    if (data.arena) localStorage.setItem(STORAGE_KEYS.arena, data.arena);
    if (data.mitra_name) localStorage.setItem(STORAGE_KEYS.mitraName, data.mitra_name);

    if (data.role === 'community') {
      const commName = data.name || data.community_user?.name || '';
      const commAvatar = data.avatar || data.community_user?.avatar || '';
      const commSlug = data.slug || data.community_user?.nickname || '';
      if (commName) localStorage.setItem('communityName', commName);
      if (commAvatar) localStorage.setItem('communityAvatar', commAvatar);
      if (commSlug) localStorage.setItem('communityNickname', commSlug);
    } else {
      ['communityName', 'communityAvatar', 'communityNickname'].forEach((key) => {
        localStorage.removeItem(key);
      });
      const clientName = data.name || data.client_name || '';
      if (clientName) localStorage.setItem(STORAGE_KEYS.clientName, clientName);
      if (data.avatar) localStorage.setItem(STORAGE_KEYS.clientAvatar, data.avatar);
    }
  }

  private clearUserMeta(): void {
    if (typeof localStorage === 'undefined') return;
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    clearArenaSlug();
  }

  getToken(): string | null {
    return getAccessToken();
  }

  getClientToken(): string | null {
    return this.isClientAuthenticated() ? getAccessToken() : null;
  }

  getSlug(): string {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem(STORAGE_KEYS.slug) ?? '';
  }

  getRole(): UserRole | string {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem(STORAGE_KEYS.role) ?? '';
  }

  isAuthenticated(): boolean {
    const role = this.getRole();
    return !!getAccessToken() && (role === 'owner' || role === 'staff');
  }

  isClientAuthenticated(): boolean {
    return !!getAccessToken() && this.getRole() === 'customer';
  }

  isAdminAuthenticated(): boolean {
    return !!getAccessToken() && this.getRole() === 'superadmin';
  }

  isCommunityAuthenticated(): boolean {
    return !!getAccessToken() && this.getRole() === 'community';
  }

  async refreshToken(_role?: string): Promise<void> {
    await this.refresh();
  }

  private toAuthError(error: unknown): Error {
    if (error instanceof ApiError) return error;
    if (
      typeof error === 'object' &&
      error !== null &&
      'response' in error &&
      typeof (error as { response?: { data?: { message?: string } } }).response?.data?.message ===
        'string'
    ) {
      return new ApiError(
        (error as { response: { data: { message: string } } }).response.data.message
      );
    }
    if (error instanceof Error) return error;
    return new ApiError('Terjadi kesalahan sistem, silakan coba lagi nanti');
  }
}

export const authService = new AuthService();
export default authService;
